import type { PetAttribute, PetEvolutionStage, PetSpeciesID } from '.';
import type { MutableState } from 'src/replicant/State';
import {
  getAttributeValue,
  getCurrentEvolution,
  getMaxPetAttributeValue,
  getPetAge,
  getPetAttributeDecayInterval,
  getPetName,
  getPetSpecies,
} from './pet.getters';
import { getRandomItemFromArray } from 'src/replicant/utils/random';
import PetNames from 'src/replicant/data/PetNames.json';
import * as Fate from '../fate';
import { fatePermanentStatusEffects } from '../fate/fate.ruleset';
import * as Encyclopedia from '../encyclopedia';
import * as Level from '../level';
import { clamp } from '../utils';
import { attributeBounds, decayFriendsCareBoost } from './pet.ruleset';
import { attributeEffectSchema } from './pet.schema';
import { PetStatusEffect } from '.';
import { getCurrentPetState, getVisiblePetState } from '../fate/fate.getters';

export const addEvolution = (
  state: MutableState,
  species: PetSpeciesID,
  stage: PetEvolutionStage,
  timestamp: number,
) => {
  state.pet.evolutions.push({ species, stage, timestamp });
};

/**
 * Clear evolution path after reborn
 */
export const resetEvolution = (state: MutableState) => {
  state.pet.evolutions = [];
};

/**
 * Save current attributes values to state
 * Used before adding new evolution to avoid attribute interpolation issues related to different species decline speed
 */
export const syncAttributes = (state: MutableState, now: number) => {
  const attributes = state.pet.attributes;
  const attributeIDs = Object.keys(attributes) as PetAttribute[];

  attributeIDs.forEach((attribute) => {
    // Get current value interpolation
    const value = getAttributeValue(state, attribute, now);

    // Save value and update updateAt value
    setAttribute(state, attribute, value, now);
  });
};

/**
 * Spawn new pet
 */
export const spawnPet = <T extends 'debut' | 'death' | 'graduation'>(
  state: MutableState,
  reason: T,
  newPet: {
    id: PetSpeciesID;
    stage: PetEvolutionStage;
    love_ini: number;
    dirtiness_ini: number;
    stomach_ini: number;
    tooth_acidity_ini: number;
    urine_ini: number;
  },
  now: number,
) => {
  const { resolveStatusEffect } = Fate.modifiers;

  // Collect history data
  const previousPetAge = getPetAge(state, now);
  const previousEvolution = getCurrentEvolution(state);
  const previousSpecies = previousEvolution?.species;
  const previousStage = previousEvolution?.stage;
  const previousPetName = getPetName(state);
  const previousPetEvolutions = [...state.pet.evolutions];

  // Flag pet as requiring new name
  state.pet.nameUpdated = false;

  // Drop previous evolutions
  resetEvolution(state);

  // Add new evolutions
  addEvolution(state, newPet.id, newPet.stage, now);

  // Set starting attributes
  setAttribute(state, 'love', newPet.love_ini, now);
  setAttribute(state, 'dirtiness', newPet.dirtiness_ini, now);
  setAttribute(state, 'stomach', newPet.stomach_ini, now);
  setAttribute(state, 'toothAcidity', newPet.tooth_acidity_ini, now);
  setAttribute(state, 'urine', newPet.urine_ini, now);

  // Clear all status effects
  resolveStatusEffect(state, 'asleep', false, now);
  resolveStatusEffect(state, 'evolving', false, now);
  resolveStatusEffect(state, 'scared', false, now);
  resolveStatusEffect(state, 'sick', false, now);
  resolveStatusEffect(state, 'toothDecay', false, now);
  resolveStatusEffect(state, 'dying', false, now);
  resolveStatusEffect(state, 'dead', false, now);
  resolveStatusEffect(state, 'hadVisitor', false, now);
  resolveStatusEffect(state, 'wantToGoOut', false, now);

  // Determine fate
  Fate.modifiers.update(state, now);

  // Update encyclopedia
  Encyclopedia.modifiers.setSpeciesState(state, newPet.id, 'unlocked');

  // Increase pets raised counter
  state.petRaised++;

  // Highest pet age
  if (state.highestPetAge < previousPetAge) {
    state.highestPetAge = previousPetAge;
  }

  // Add XP reward
  if (reason !== 'graduation') {
    Level.modifiers.incrementXp(state, reason, previousStage!);
  }

  return {
    species: previousSpecies,
    name: previousPetName,
    age: previousPetAge,
    evolutions: previousPetEvolutions,
  };
};

export function setAttribute(
  state: MutableState,
  attribute: PetAttribute,
  value: number,
  now: number,
): void {
  const attributeValue = state.pet.attributes[attribute];
  attributeValue.value = clamp(
    value,
    attributeBounds.min,
    getMaxPetAttributeValue(),
  );
  attributeValue.lastUpdatedAt = now;
}

/**
 * Set temporary debuff or buff attribute effect
 *
 * @param state - User state
 * @param attribute - What attribute get debuff effect
 * @param duration - For what time effect will affect attribute calculation
 * @param decay - Attribute multiplier, for example: -2 slow attribute decrease, 2 speedup attribute decrease
 * @param now - From what time we start this effect
 */
export function addAttributeEffectDuration(
  { pet }: MutableState,
  attribute: PetAttribute,
  duration: number,
  decay: number,
  now: number,
): void {
  const attributeEffect = pet.attributesEffects[attribute];
  const previousEffectEnd = attributeEffect.startAt + attributeEffect.duration;
  const isResolved = !attributeEffect || previousEffectEnd <= now;

  // Previous buff or debuff are finished, simple start new buff
  if (isResolved) {
    attributeEffect.duration = duration;
    attributeEffect.decay = decay;
    attributeEffect.startAt = now;
    return;
  }

  // If we have on going status effect
  // We need to merge them together depends on duration proportions

  // Duration of previous effect + new effect duration
  const durationSum = attributeEffect.duration + duration;

  // Detect previous and new decay value based on percentage duration of new status effect
  const previousDecay =
    attributeEffect.decay / (durationSum / attributeEffect.duration);
  const newDecay = decay / (durationSum / duration);

  // Get approximated decal value
  const approximatedDecay = previousDecay + newDecay;

  // Set approximated effect for extended duration
  attributeEffect.duration = durationSum;
  attributeEffect.decay = approximatedDecay;
}

/**
 * Apply friend visit debuff
 *
 * @spec https://docs.google.com/document/d/1xpgNbY47Z0W674SxSsrqHolgNVsE_V5rQMs4_X_RT_k
 */
export function applyFriendVisitDebuff(
  state: MutableState,
  attribute: PetAttribute,
  effect: number,
  now: number,
): void {
  const petDecayInterval = getPetAttributeDecayInterval(state, attribute);

  // Speed up decay for x5 (-1 (Default decay) + -1 (Boost decay) = -5x)
  const boost = decayFriendsCareBoost[attribute];

  // Formula: Boost time = PetSpecies.{stomach | love}_sec * effect_by_friend / boost
  const duration = petDecayInterval * (effect / boost);

  // We already have decay -1 by default, if won't subtract -1 here we will get not expect x2 but x3
  const decay = boost - 1;

  // Add debuff effect for selected attribute
  addAttributeEffectDuration(state, attribute, duration, -decay, now);
}

export function removeFriendVisitDebuffs(state: MutableState): void {
  state.pet.attributesEffects.love = attributeEffectSchema.getDefault()!;
  state.pet.attributesEffects.stomach = attributeEffectSchema.getDefault()!;
}

export function setStatusEffect(
  state: MutableState,
  statusEffect: PetStatusEffect,
  value: true,
  now: number,
  expiresAt?: number,
): void;
// eslint-disable-next-line no-redeclare
export function setStatusEffect(
  state: MutableState,
  statusEffect: PetStatusEffect,
  value: boolean,
  now: number,
): void;
/**
 * Only for internal use!
 * Use fate.resolveStatusEffect() instead
 *
 * @deprecated
 */
// eslint-disable-next-line no-redeclare
export function setStatusEffect(
  state: MutableState,
  statusEffect: PetStatusEffect,
  value: boolean,
  now: number,
  expiresAt?: number,
): void {
  const effect = state.pet.statusEffects[statusEffect];
  const oldValue = effect.value;
  effect.value = value;
  effect.lastUpdatedAt = now;

  // A/B 0468 V2: record real wantToInvite fires (false → true transitions)
  // so the invite gate can detect "never invited" reliably. `lastUpdatedAt`
  // alone isn't safe — sync can write it speculatively before any real fire.
  if (statusEffect === 'wantToInvite' && !oldValue && value) {
    state.pet.lastWantToInviteFireAt = now;
  }

  if (value) {
    // Set effect expiration timestamp if specified
    if (expiresAt) {
      effect.expiresAt = expiresAt;
    }
  }
  // Clear resolution attempts if the effect was resolved
  else if (oldValue) {
    effect.resolutionAttempts = 0;
  }
}

export function incrementResolutionAttempts(
  { pet }: MutableState,
  statusEffect: PetStatusEffect,
): void {
  pet.statusEffects[statusEffect].resolutionAttempts += 1;
}

export function acceptEvolution(state: MutableState, now: number): void {
  const evolution = getCurrentEvolution(state);

  if (!evolution) {
    throw new Error('No evolution to accept');
  }

  const { species } = evolution;
  Encyclopedia.modifiers.setSpeciesState(state, species, 'unlocked');

  delete state.evolutionRewind;
}

export function makePetHappy(state: MutableState, now: number): void {
  // Resolve all status effects.
  fatePermanentStatusEffects.forEach((effect) => {
    Fate.modifiers.resolveStatusEffect(state, effect, false, now);
  });

  // Reset pet stats.
  setAttribute(state, 'stomach', 100, now);
  setAttribute(state, 'love', 100, now);
  setAttribute(state, 'dirtiness', 0, now);
  setAttribute(state, 'toothAcidity', 0, now);
  setAttribute(state, 'urine', 0, now);

  Fate.modifiers.update(state, now);
}

/**
 * Create user first pet
 */
export function createFirstPet(
  state: MutableState,
  now: number,
  random: () => number,
): void {
  const babyPets = getPetSpecies(state).filter((pet) => pet.stage === 'baby');
  const newPet = babyPets[Math.floor(babyPets.length * random())];

  // Assign pet name
  const localeSuffix = state.settings.locale === 'ja' ? 'jp' : 'en';
  state.pet.name = getRandomItemFromArray(PetNames, random)[
    `name_${localeSuffix}`
  ];
  state.pet.nameUpdated = true;

  const { resolveStatusEffect } = Fate.modifiers;

  // Add first evolution
  addEvolution(state, newPet.id, newPet.stage, now);

  // Set starting attributes
  setAttribute(state, 'love', newPet.love_ini!, now);
  setAttribute(state, 'dirtiness', newPet.dirtiness_ini!, now);
  setAttribute(state, 'stomach', newPet.stomach_ini!, now);
  setAttribute(state, 'toothAcidity', newPet.tooth_acidity_ini!, now);
  setAttribute(state, 'urine', newPet.urine_ini!, now);

  // Clear all status effects
  resolveStatusEffect(state, 'asleep', false, now);
  resolveStatusEffect(state, 'evolving', false, now);
  resolveStatusEffect(state, 'scared', false, now);
  resolveStatusEffect(state, 'sick', false, now);
  resolveStatusEffect(state, 'toothDecay', false, now);
  resolveStatusEffect(state, 'dying', false, now);
  resolveStatusEffect(state, 'dead', false, now);
  resolveStatusEffect(state, 'hadVisitor', false, now);
  resolveStatusEffect(state, 'wantToGoOut', false, now);

  // Determine fate
  Fate.modifiers.update(state, now);

  Encyclopedia.modifiers.setSpeciesState(state, newPet.id, 'unlocked');
}

/**
 * Revive pet to initial status values, only if it's dead
 * @param state
 * @param now
 * @returns true if pet was revived, false otherwise
 */
export function revivePet(state: MutableState, now: number): boolean {
  // Only revive if there is a pet
  const pet = getCurrentEvolution(state);
  if (!pet) return false;

  // Only revive if the pet is dead
  const petStatus = getCurrentPetState(state, now, false);
  if (petStatus !== 'dead') return false;

  // Resolve all negative status effects.
  fatePermanentStatusEffects.forEach((effect) => {
    Fate.modifiers.resolveStatusEffect(state, effect, false, now);
  });

  // Reset pet stats.
  setAttribute(state, 'stomach', 39, now);
  setAttribute(state, 'love', 41, now);
  setAttribute(state, 'dirtiness', 0, now);
  setAttribute(state, 'toothAcidity', 0, now);
  setAttribute(state, 'urine', 0, now);

  // Update pet's fate
  Fate.modifiers.update(state, now);

  // Pet has been revived
  return true;
}
